/** @format */

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	IAccountVerificationPayload,
	IAccountVerification,
} from "../../utility/types/circleType";
import {
	completeIdentityVerification,
	createAccountLink,
	getAccountRequirement,
	getAccountVerificationStatus,
	getIdentityVerificationSession,
	StripeAccountDetailsResponse,
	updateStripeAccountDetails,
} from "../actions/circleAction";
import { IUserVerification } from "../../components/verifyAndConfirmAccount/types";

export interface IdentitySessionState {
	clientSecret: string | null;
	sessionUrl: string | null;
	status: string | null;
	processing: boolean;
	alreadyVerified: boolean;
	isLoading: boolean;
	error: string | null;
}

interface AccountLinkState {
	linkUrl: string | null;
	expiresAt?: number;
	isLoading: boolean;
	error: string | null;
}

export interface RequirementsState {
	currently_due: string[];
	past_due: string[];
	eventually_due: string[];
	isLoading: boolean;
	error: string | null;
	identityVerificationStatus:
	| "pending"
	| "processing"
	| "verified"
	| "requires_action"
	| "none";
	isUserVerified: boolean;
	// Country-specific configuration
	countryConfig: {
		code: string;
		name: string;
		requiresSSN: boolean;
		nationalIdLabel: string;
		currency: string;
	} | null;
}

export interface StripeAccountRequirementDetailState {
	external_account: any | null;
	representative: any | null;
}

const initialRequirementsState: RequirementsState = {
	currently_due: [],
	past_due: [],
	eventually_due: [],
	isLoading: false,
	error: null,
	identityVerificationStatus: "none",
	isUserVerified: false,
	countryConfig: null,
};

const initialDetailState: StripeAccountRequirementDetailState = {
	external_account: "",
	representative: "",
};

export interface AccountVerificationState {
	accountVerificationPayload: IAccountVerificationPayload | null;
	accountVerificationData: IUserVerification | null;
	identitySession: IdentitySessionState;
	completing: boolean;
	completeError: string;
	isLoading: boolean;
	error: string;
	accountLink: AccountLinkState;
	requirements: RequirementsState;
	detailState: StripeAccountRequirementDetailState;
}

const initialAccountVerificationState: AccountVerificationState = {
	accountVerificationData: null,
	accountVerificationPayload: null,
	identitySession: {
		clientSecret: null,
		sessionUrl: null,
		status: null,
		processing: false,
		alreadyVerified: false,
		isLoading: false,
		error: null,
	},
	accountLink: {
		linkUrl: null,
		expiresAt: undefined,
		isLoading: false,
		error: null,
	},
	requirements: initialRequirementsState,
	detailState: initialDetailState,
	completing: false,
	completeError: "",
	isLoading: false,
	error: "",
};

export const accountVerificationSlice = createSlice({
	name: "accountVerification",
	initialState: initialAccountVerificationState,
	reducers: {
		setAccountVerificationData: (
			state,
			action: PayloadAction<IAccountVerificationPayload>,
		) => {
			state.accountVerificationPayload = action.payload;
		},
	},

	extraReducers: (builder) => {
		builder.addCase(
			getAccountVerificationStatus.pending,
			(state, action: PayloadAction<any>) => {
				state.isLoading = true;
			},
		);

		builder
			.addCase(getIdentityVerificationSession.pending, (state) => {
				state.identitySession.isLoading = true;
				state.identitySession.error = null;
				// Clear the previous URL so a stale one cannot trigger an immediate redirect.
				state.identitySession.sessionUrl = null;
				state.identitySession.status = null;
				state.identitySession.processing = false;
				state.identitySession.alreadyVerified = false;
			})
			.addCase(
				getIdentityVerificationSession.fulfilled,
				(
					state,
					action: PayloadAction<{
						clientSecret: string;
						sessionUrl: string | null;
						status?: string;
						processing?: boolean;
						alreadyVerified?: boolean;
					}>,
				) => {
					state.identitySession.isLoading = false;
					state.identitySession.clientSecret = action.payload.clientSecret;
					state.identitySession.sessionUrl = action.payload.sessionUrl ?? null;
					state.identitySession.status = action.payload.status ?? null;
					state.identitySession.processing = Boolean(action.payload.processing);
					state.identitySession.alreadyVerified = Boolean(
						action.payload.alreadyVerified,
					);
				},
			)
			.addCase(getIdentityVerificationSession.rejected, (state, action) => {
				state.identitySession.isLoading = false;
				state.identitySession.error =
					(action.payload as string) || "Failed to start identity session";
			})
			// completeSession
			.addCase(completeIdentityVerification.pending, (state) => {
				state.completing = true;
				state.completeError = "";
			})
			.addCase(completeIdentityVerification.fulfilled, (state) => {
				state.completing = false;
			})
			.addCase(completeIdentityVerification.rejected, (state, action) => {
				state.completing = false;
				state.completeError =
					(action.payload as { message?: string })?.message ||
					"Failed to complete verification";
			});

		builder.addCase(
			getAccountVerificationStatus.fulfilled,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.accountVerificationData = action.payload;
			},
		);

		builder.addCase(
			getAccountVerificationStatus.rejected,
			(state, action: PayloadAction<any>) => {
				state.isLoading = false;
				state.error =
					action.payload ||
					"Failed to send  getVerificationStatus request. Unknown error occurred";
			},
		);

		builder
			.addCase(createAccountLink.pending, (state) => {
				state.accountLink.isLoading = true;
				state.accountLink.error = null;
			})
			.addCase(
				createAccountLink.fulfilled,
				(
					state,
					action: PayloadAction<{ accountLinkUrl: string; expiresAt?: number }>,
				) => {
					state.accountLink.isLoading = false;
					state.accountLink.linkUrl = action.payload.accountLinkUrl;
					state.accountLink.expiresAt = action.payload.expiresAt;
				},
			)
			.addCase(createAccountLink.rejected, (state, action) => {
				state.accountLink.isLoading = false;
				state.accountLink.error = action.payload || "Failed to load link";
			});

		builder
			.addCase(getAccountRequirement.pending, (state) => {
				state.requirements.isLoading = true;
				state.requirements.error = null;
			})
			.addCase(
				getAccountRequirement.fulfilled,
				(state, action: PayloadAction<StripeAccountDetailsResponse>) => {
					state.requirements.isLoading = false;
					// Safely access requirements with null checks
					const requirements = action.payload?.requirements;
					const details = action.payload?.details;
					state.requirements.currently_due = requirements?.currently_due ?? [];
					state.requirements.past_due = requirements?.past_due ?? [];
					state.requirements.eventually_due =
						requirements?.eventually_due ?? [];
					state.requirements.identityVerificationStatus =
						action.payload?.identityVerificationStatus ?? "none";
					state.requirements.isUserVerified =
						action.payload?.isUserVerified ?? false;
					state.requirements.countryConfig =
						action.payload?.countryConfig ?? null;
					state.detailState.external_account =
						details?.external_account ?? null;
					state.detailState.representative = details?.representative ?? null;
				},
			)
			.addCase(getAccountRequirement.rejected, (state, action) => {
				state.requirements.isLoading = false;
				state.requirements.error = action.payload || "Unknown error";
			});

		builder
			.addCase(updateStripeAccountDetails.pending, (s) => {
				s.requirements.isLoading = true;
				s.requirements.error = null;
			})
			.addCase(updateStripeAccountDetails.fulfilled, (s) => {
				s.requirements.isLoading = false;
				// we’ll re‑fetch requirements after
			})
			.addCase(updateStripeAccountDetails.rejected, (s, a) => {
				s.requirements.isLoading = false;
				s.requirements.error = a.payload ?? "Failed to save details";
			});
	},
});

export const { setAccountVerificationData } = accountVerificationSlice.actions;

export default accountVerificationSlice.reducer;
